<template lang="html">
    <div
        class="map-filters"
        :style="cssVars"
    >
        <b-button
            class="is-hidden-tablet open-filters-button"
            @click="mobileFiltersOpen = true"
        >
            <b-icon
                pack="fas"
                icon="sliders-h"
                size="is-small"
            />
        </b-button>
        <div
            :class="[
                'filters-container',
                'has-background-white',
                'box',
                {'popup-open': mobileFiltersOpen}
            ]"
        >
            <div class="header">
                <b-icon
                    pack="fas"
                    icon="sliders-h"
                    size="is-small"
                    class="filter-icon"
                />
                <h2 class="title is-3">
                    Allocations
                </h2>
                <b-button
                    class="close is-hidden-tablet"
                    @click="mobileFiltersOpen = false"
                >
                    <b-icon
                        pack="mdi"
                        icon="close"
                        size="is-small"
                    />
                </b-button>
            </div>

            <div
                v-if="topLevelFilter.filters"
                class="top-level-filters"
            >
                <div
                    v-for="type in topLevelFilter.filters"
                    :key="type.name"
                >
                    <b-tooltip
                        class="type-tooltip"
                        type="is-light"
                        :delay="500"
                        :label="type.tooltip"
                        :active="!!type.tooltip"
                        multilined
                        size="is-small"
                    >
                        <b-button
                            :type="{'selected': topLevelFilter.selected.includes(type.name)}"
                            @click="clickTopLevelFilter(type.name)"
                            @click.native="topLevelClickEvent(
                                type.displayText,
                                topLevelFilter.selected.includes(type.name)
                            )"
                        >
                            <span
                                class="licence-marker"
                                :style="`background-color: ${type.color};`"
                            />{{ type.displayText }}
                        </b-button>
                    </b-tooltip>
                </div>
            </div>

            <div
                v-if="checkboxGroups.length || dropdownGroups.length"
                class="filter-groups-section"
            >
                <h5 class="title is-5 is-hidden-mobile">
                    Filters
                </h5>

                <div
                    v-if="checkboxGroups.length"
                    class="filter-groups is-hidden-mobile"
                >
                    <b-button
                        v-for="(checkboxGroup, index) in checkboxGroups"
                        :key="`checkboxGroup-${checkboxGroup.name}`"
                        :type="{'is-primary': isCheckboxGroupSelected(index)}"
                        :outlined="!isCheckboxGroupSelected(index) && isCheckboxGroupAltered(index)"
                        class="filter-group"
                        @click="clickCheckboxGroup(checkboxGroup.name)"
                    >
                        {{ checkboxGroup.displayText }}
                    </b-button>
                    <div
                        v-for="(checkboxGroup, checkboxGroupIndex) in checkboxGroups"
                        :key="`checkboxGroup-${checkboxGroup.name}-checkboxes`"
                        class="is-hidden-mobile"
                    >
                        <div
                            :class="[
                                checkboxGroup.name,
                                'checkbox-group-filters',
                                'has-background-white',
                                'box',
                                {'group-selected': selectedCheckboxGroup === checkboxGroup.name}
                            ]"
                        >
                            <div
                                :class="[
                                    'checkboxes-container',
                                    {'single-column': checkboxGroup.singleColumn || checkboxGroup.filters.length === 1}
                                ]"
                            >
                                <b-checkbox
                                    v-for="(filter) in checkboxGroup.filters"
                                    :key="`filter-${filter.name}`"
                                    v-model="checkboxGroup.selected"
                                    :native-value="filter.name"
                                    type="default"
                                    class="checkbox-filter"
                                    @input="(newSelected) => checkboxClickEvent(
                                        checkboxGroup.displayText,
                                        filter.displayText,
                                        newSelected.includes(filter.name)
                                    )"
                                >
                                    {{ filter.displayText }}
                                </b-checkbox>
                            </div>
                            <div class="filter-group-buttons">
                                <b-button
                                    type="is-light"
                                    @click="resetCheckboxGroup(checkboxGroupIndex)"
                                >
                                    Reset
                                </b-button>
                                <b-button
                                    type="is-primary"
                                    @click="clickCheckboxGroup(checkboxGroup.name)"
                                >
                                    Save
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-for="(checkboxGroup) in checkboxGroups"
                    :key="`checkboxGroup-${checkboxGroup.name}-options`"
                    class="is-hidden-tablet"
                >
                    <h5 class="title is-5">
                        {{ checkboxGroup.displayText }}
                    </h5>
                    <div
                        :class="[
                            checkboxGroup.name,
                            'checkbox-group-filters',
                            { 'group-selected': selectedCheckboxGroup === checkboxGroup.name }
                        ]"
                    >
                        <div class="checkboxes-container">
                            <b-checkbox
                                v-for="(filter) in checkboxGroup.filters"
                                :key="`filter-${filter.name}-checkboxes`"
                                v-model="checkboxGroup.selected"
                                :native-value="filter.name"
                                type="default"
                                class="checkbox-filter"
                                @input="(newSelected) => checkboxClickEvent(
                                    checkboxGroup.displayText,
                                    filter.displayText,
                                    newSelected.includes(filter.name)
                                )"
                            >
                                {{ filter.displayText }}
                            </b-checkbox>
                        </div>
                    </div>
                </div>

                <div
                    v-if="dropdownGroups.length"
                    class="filter-groups is-hidden-mobile"
                >
                    <b-button
                        v-for="(dropdownGroup, index) in dropdownGroups"
                        :key="`dropdownGroup-${dropdownGroup.name}`"
                        :type="{'is-primary': isDropdownGroupSelected(index)}"
                        :outlined="!isDropdownGroupSelected(index) && isDropdownGroupAltered(index)"
                        class="filter-group"
                        @click="clickDropdownGroup(dropdownGroup.name)"
                    >
                        {{ dropdownGroup.displayText }}
                    </b-button>
                    <div
                        v-for="(dropdownGroup, dropdownGroupIndex) in dropdownGroups"
                        :key="`dropdownGroup-${dropdownGroup.name}-selects`"
                        class="is-hidden-mobile"
                    >
                        <div
                            :class="[
                                dropdownGroup.name,
                                'dropdown-group-filters',
                                'has-background-white',
                                'box',
                                {'select-selected': selectedDropdownGroup === dropdownGroup.name}
                            ]"
                        >
                            <div
                                :class="[
                                    'dropdowns-container',
                                    {'single-column': dropdownGroup.singleColumn || dropdownGroup.filters.length === 1}
                                ]"
                            >
                                <div
                                    v-for="filter in dropdownGroup.filters"
                                    :key="`filter-${filter.name}`"
                                    class="dropdown-filter"
                                >
                                    <div>
                                        {{ filter.displayText }}
                                    </div>
                                    <b-select
                                        v-model="filter.selected"
                                        @input="(value) => dropdownClickEvent(
                                            dropdownGroup.displayText,
                                            filter.displayText, value
                                        )"
                                    >
                                        <option
                                            v-for="option in filter.dropdownOptions"
                                            :key="`filter-${filter.name}-option-${option.name}`"
                                            :value="option.name"
                                            class="dropdown-filter-option"
                                        >
                                            {{ option.displayText }}
                                        </option>
                                    </b-select>
                                </div>
                            </div>
                            <div class="filter-group-buttons">
                                <b-button
                                    type="is-light"
                                    @click="resetDropdownFilter(dropdownGroupIndex)"
                                >
                                    Reset
                                </b-button>
                                <b-button
                                    type="is-primary"
                                    @click="clickDropdownGroup(dropdownGroup.name)"
                                >
                                    Save
                                </b-button>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    v-for="dropdownGroup in dropdownGroups"
                    :key="`dropdownGroup-${dropdownGroup.name}-options`"
                    class="is-hidden-tablet"
                >
                    <h5 class="title is-5">
                        {{ dropdownGroup.displayText }}
                    </h5>
                    <div
                        :class="[
                            dropdownGroup.name,
                            'dropdown-group-filters',
                            { 'select-selected': selectedDropdownGroup === dropdownGroup.name }
                        ]"
                    >
                        <div class="dropdowns-container">
                            <div
                                v-for="filter in dropdownGroup.filters"
                                :key="`filter-${filter.name}`"
                                class="dropdown-filter"
                            >
                                <div>
                                    {{ filter.displayText }}
                                </div>
                                <b-select
                                    v-model="filter.selected"
                                    @input="(value) => dropdownClickEvent(
                                        dropdownGroup.displayText,
                                        filter.displayText, value
                                    )"
                                >
                                    <option
                                        v-for="option in filter.dropdownOptions"
                                        :key="`filter-${filter.name}-option-${option.name}`"
                                        :value="option.name"
                                        class="dropdown-filter-option"
                                    >
                                        {{ option.displayText }}
                                    </option>
                                </b-select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="action-buttons">
                <b-button
                    type="is-primary"
                    class="is-hidden-tablet return-to-map"
                    @click="mobileFiltersOpen = false"
                >
                    Return To Map
                </b-button>
                <b-button
                    type="is-light"
                    class="reset"
                    @click="resetAllFilters"
                >
                    Reset Filters
                </b-button>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MapFilters',

    props: {
        givenTopLevelFilter: {
            type: Object,
            required: false,
            default: () => {},
        },
        givenCheckboxGroups: {
            type: Array,
            required: false,
            default: () => [],
        },
        givenDropdownGroups: {
            type: Array,
            required: false,
            default: () => [],
        },
    },

    data: () => ({
        cssVars: {},
        mobileFiltersOpen: false,
        selectedCheckboxGroup: null,
        selectedDropdownGroup: null,
        topLevelFilter: {
            selected: [],
            filters: [],
        },
        checkboxGroups: [],
        dropdownGroups: [],
    }),

    computed: {
        // aggregate all selected filters into one var for watching
        selectedFilters() {
            return [
                ...this.checkboxGroups.map((group) => group.selected),
                ...this.dropdownGroups.map((group) => group.filters.map((filter) => filter.selected)),
            ];
        },
    },

    watch: {
        // when any filter selection changes, rebuild the expression
        selectedFilters() {
            this.buildExpression();
        },
    },

    mounted() {
        // we need to calculate the scrollbar width so that on narrow screens we can set the popup window to the
        // correct size, as the vw css property does not take the scrollbar's width into account.
        const bodyWidth = document.body.clientWidth;
        const windowWidth = window.innerWidth;
        if (windowWidth - bodyWidth > 5) {
            this.cssVars['--scrollbar-width'] = `${windowWidth - bodyWidth}px`;
        } else {
            // if the scrollbar is less than 5px wide, assume we're on mobile and that it's a temporary scollbar that
            // won't affect the width of the container.
            this.cssVars['--scrollbar-width'] = '0px';
        }

        this.topLevelFilter = { ...this.givenTopLevelFilter };
        this.checkboxGroups = [...this.givenCheckboxGroups];
        this.dropdownGroups = [...this.givenDropdownGroups];

        this.buildExpression();
    },

    methods: {
        /**
         * Handle click on one of the top level filter buttons.
         * @param {string} type - the name of the top level filter that was clicked
         */
        clickTopLevelFilter(type) {
            const index = this.topLevelFilter.selected.indexOf(type);
            if (index > -1) {
                this.topLevelFilter.selected.splice(index, 1);
            } else {
                this.topLevelFilter.selected.push(type);
            }

            this.buildExpression();
        },

        /**
         * Handle click on one of the checkbox group buttons.
         * @param {string} group - the name of the checkbox group that was clicked
         */
        clickCheckboxGroup(group) {
            // ensure any open dropdown group is closed
            this.selectedDropdownGroup = null;

            if (this.selectedCheckboxGroup === group) {
                this.selectedCheckboxGroup = null;
            } else {
                this.selectedCheckboxGroup = group;
            }
        },

        /**
         * Determine if a checkboxGroup is currently selected
         * @param {number} checkboxGroupIndex - the index of the checkboxGroup to check
         * @returns {boolean} - value indicating if checkboxGroup is selected.
         */
        isCheckboxGroupSelected(checkboxGroupIndex) {
            const { name } = this.checkboxGroups[checkboxGroupIndex];
            return this.selectedCheckboxGroup === name;
        },

        /**
         * Determine if a checkboxGroup's filters have been altered
         * @param {number} checkboxGroupIndex - the index of the checkboxGroup to check
         * @returns {boolean} - value indicating if checkboxGroup's filters have been altered
         */
        isCheckboxGroupAltered(checkboxGroupIndex) {
            const { selected, filters } = this.checkboxGroups[checkboxGroupIndex];
            return selected.length !== filters.length;
        },

        /**
         * Handle click on one of the dropdown group buttons.
         * @param {string} select - the name of the dropdown group that was clicked
         */
        clickDropdownGroup(select) {
            // ensure any open checkbox group popup is closed
            this.selectedCheckboxGroup = null;

            if (this.selectedDropdownGroup === select) {
                this.selectedDropdownGroup = null;
            } else {
                this.selectedDropdownGroup = select;
            }
        },

        /**
         * Determine if a dropdownGroup is currently selected
         * @param {number} dropdownGroupIndex - the index of the dropdownGroup to check
         * @returns {boolean} - value indicating if dropdownGroup is selected.
         */
        isDropdownGroupSelected(dropdownGroupIndex) {
            const { name } = this.dropdownGroups[dropdownGroupIndex];
            return this.selectedDropdownGroup === name;
        },

        /**
         * Determine if a dropdownGroup's filters have been altered
         * @param {number} dropdownGroupIndex - the index of the dropdownGroup to check
         * @returns {boolean} - value indicating if dropdownGroup's filters have been altered
         */
        isDropdownGroupAltered(dropdownGroupIndex) {
            const { filters } = this.dropdownGroups[dropdownGroupIndex];
            return filters.some((filter) => filter.selected !== 'all');
        },

        /**
         * Reset filters in a specified checkbox group.
         * @param {number} index - index of checkbox group to reset
         */
        resetCheckboxGroup(index) {
            this.checkboxGroups[index].selected = this.checkboxGroups[index].filters.map((filter) => filter.name);
        },

        /**
         * Reset filters in a specified dropdown group.
         * @param {number} index - index of dropdown group to reset
         */
        resetDropdownFilter(index) {
            this.dropdownGroups[index].filters.forEach((filter, fitlerIndex) => {
                this.dropdownGroups[index].filters[fitlerIndex].selected = 'all';
            });
        },

        /**
         * Reset all the filters.
        */
        resetAllFilters() {
            this.topLevelFilter.selected = this.topLevelFilter.filters.map((filter) => filter.name);
            this.checkboxGroups.forEach((checkboxGroup, checkboxGroupIndex) => {
                this.resetCheckboxGroup(checkboxGroupIndex);
            });
            this.dropdownGroups.forEach((dropdownGroup, dropdownGroupIndex) => {
                this.resetDropdownFilter(dropdownGroupIndex);
            });

            this.buildExpression();
        },

        /**
         * Loop through each selected filter and build a filter expression, that is then emitted in an event.
         */
        buildExpression() {
            const allFilterExpressions = ['all'];

            const allFilterGroups = this.topLevelFilter.filters ? [this.topLevelFilter] : [];
            allFilterGroups.push(...this.checkboxGroups);
            allFilterGroups.forEach((filterGroup) => {
                // for each selected filter group, put expression into filterExpressions
                const filterExpressions = [];
                filterGroup.filters.forEach((filter) => {
                    if (filterGroup.selected.includes(filter.name)) {
                        filterExpressions.push(filter.expression);
                    }
                });
                // build expression for this filter type
                if (filterExpressions.length > 0) {
                    allFilterExpressions.push([
                        'any',
                        ...filterExpressions,
                    ]);
                } else {
                    // if no options are selected, set something to match 0 features
                    allFilterExpressions.push([
                        'all',
                        ['==', ['get', 'term'], 'neverGonnaGiveYouUp'],
                    ]);
                }
            });

            // need to work through dropdown groups separately, as the logic differs
            this.dropdownGroups.forEach((dropdownGroup) => {
                dropdownGroup.filters.forEach((filter) => {
                    const selectedOption = filter.dropdownOptions.find((option) => option.name === filter.selected);
                    allFilterExpressions.push(selectedOption.expression);
                });
            });

            this.$emit('change', allFilterExpressions);
        },

        /**
         * Triggers a tracking event when a filter is changed
         * Currently setup for boolean filters only
         * @param {String} clickedFilterName - filter user has toggled
         * @param {String} filterGroupType - type of filter group being changed
         * @param {Array} selectedFilters - selected filters for checkboxGroup received from input event. Only received
         *                                  when filter from checkboxGroups is clicked.
         * @param {number} checkboxGroupIndex - the index of the checkboxGroup that the clicked filter is part of
         *
         */
        clickEvent(eventText) {
            if (this.$ga) {
                this.$ga.event('Map', 'filterchange', eventText);
            }
        },

        topLevelClickEvent(topLevelFilterName, applied) {
            const eventText = `Changed: ${applied ? 'Applied' : 'Removed'} ${topLevelFilterName} Filter`;
            this.clickEvent(eventText);
        },

        checkboxClickEvent(checkboxGroupName, changedFilterName, applied) {
            const eventText = `Changed: ${checkboxGroupName} Filter -> ${changedFilterName} ${applied ? 'applied' : 'removed'}`;
            this.clickEvent(eventText);
        },

        dropdownClickEvent(dropdownGroupName, changedFilterName, filterOptionSelected) {
            const eventText = `Changed: ${dropdownGroupName} - ${changedFilterName} Filter -> ${filterOptionSelected}`;
            this.clickEvent(eventText);
        },
    },
};
</script>

<style lang="scss">
 @import '~bulma/sass/utilities/mixins';

.map-filters {
    $tablet: 769px !default;
    $primary: #33284a !default;

    .filters-container {
        min-width: 325px;

        .button {
            &.is-primary {
                color: white;
            }
        }

        .header {
            display: flex;
            align-items: center;
            margin-bottom: 1.5em;

            .filter-icon {
                margin-right: 1em;
            }

            .title {
                flex: 1;
                margin: 0;
            }
        }

        .top-level-filters {
            margin: 1em 0;
            display: grid;
            grid-template-columns: 1fr 1fr;
            grid-gap: 0.75em;
            justify-content: space-between;

            .type-tooltip {
                width: 100%;

                .button {
                    width: 100%;

                    .licence-marker {
                        width: 12px;
                        height: 12px;
                        border-radius: 6px;
                        display: inline-block;
                        margin-right: 8px;
                        margin-bottom: -1px;
                    }

                    &:not(.selected) {
                        color: grey;

                        .licence-marker {
                            background-color: grey !important;
                        }
                    }
                }
            }
        }

        .filter-groups-section {
            position: relative;

            .title {
                margin-bottom: 0.5em;
            }

            .filter-groups {
                display: flex;
                margin: 0 -0.5em 0.5em -0.5em;

                // linter gets upset that this is below the ".top-level-filters .type-tooltip .button:not(.selected)"
                // selector. The linter doesn't have access to the html, and so can't see that the two rules will never
                // match the same thing. In cases where the rule is catching something that isn't actually an issue,
                // the docs recommend to just disable the rule for the line that's causing the issue.
                // stylelint-disable-next-line no-descending-specificity
                .button {
                    flex: 1;
                    height: 35px;
                    margin: 0 0.5em;
                    padding-right: 0.5em;
                    padding-left: 0.5em;

                    &.is-outlined {
                        border-color: $primary;
                        color: $primary;
                        font-weight: 500;
                    }
                }
            }

            .dropdown-group-filters,
            .checkbox-group-filters {
                display: none;
                position: absolute;
                top: 73px;
                right: 0;
                width: 100%;
                z-index: 10;

                .checkboxes-container,
                .dropdowns-container {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    grid-auto-flow: row;
                    grid-gap: 0.5em 0.25em;
                    justify-items: start;
                    margin-bottom: 0.75em;

                    &.single-column {
                        grid-template-columns: 1fr;
                    }
                }

                &.group-selected,
                &.select-selected {
                    display: block;
                }

                &.select-selected {
                    top: 116px;
                }

                .dropdown-filter {
                    width: 100%;

                    span.select,
                    select {
                        width: 100%;
                    }
                }

                .checkbox {
                    margin: 0;

                    .control-label {
                        padding-left: 0.3em;
                    }
                }

                .filter-group-buttons {
                    width: 100%;
                    display: flex;
                    justify-content: space-around;
                    margin-top: 0.5em;

                    .button {
                        width: 5em;
                    }
                }
            }
        }

        .action-buttons {
            margin-top: 0.5em;
        }
    }

    @include mobile {
        .open-filters-button {
            .icon {
                margin-top: 5px;
            }
        }

        .filters-container {
            display: none;
            height: 100%;
            width: calc(100vw - var(--scrollbar-width));
            overflow: auto;
            border-radius: 0;

            &.popup-open {
                display: flex;
                flex-direction: column;
                position: absolute;
                right: 0;
                top: 0;
                z-index: 100;
                margin: 0;
                padding: 1em;

                .header {
                    .button.close {
                        border: none;

                        .icon {
                            margin-top: 0.2em;
                            margin-right: -0.8em;
                        }
                    }
                }

                .filter-groups-section {
                    flex: 1;

                    .dropdown-group-filters,
                    .checkbox-group-filters {
                        display: block;
                        position: relative;
                        top: 0;
                    }
                }

                .action-buttons {
                    width: 100%;
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 1em;

                    .button {
                        height: 3em;
                        width: 45%;
                    }
                }
            }
        }
    }
}
</style>
